import { useState, useRef, useCallback, useEffect } from "react"
import { useLocation } from "wouter"
import IPhoneDevice from "../components/IPhoneDevice"

// Node.contains() throws "parameter 1 is not of type 'Node'" if handed
// anything that isn't a Node, and a mouseleave's relatedTarget is null
// whenever the pointer exits toward browser chrome or off the window
// entirely. Unguarded, that throws inside React's event dispatch and
// surfaces as an uncaught runtime error.
const containsNode = (parent, target) =>
  target instanceof Node && !!parent && parent.contains(target)

const PROJECTS = [
  {
    id: "boost",
    year: "2025",
    company: "OKX",
    role: "Design Director",
    link: "/case/okx",
    title: "Web3 Growth — Boost",
    caption: "Web3・0→1・Led vision・Design end to end",
    description: "A viral growth mechanic that turns every trade into a moment worth sharing. Boost brought referral loops, streak rewards, and live leaderboards into OKX's core product — driving user acquisition without eroding trust.",
    gif: "/2026/01_latest_boost.gif",
    video: "/2026/latest_boost.mp4",
  },
  {
    id: "defi",
    year: "2024",
    company: "OKX",
    role: "Design Director",
    link: "/case/defi",
    title: "Web3 DeFi 2.0 Revamp",
    caption: "DeFi・Redesign・Design end to end",
    description: "Redesigning decentralised finance to feel as intuitive as a consumer banking app. DeFi 2.0 abstracted wallet complexity, surfaced yields in plain language, and introduced a one-tap swap experience adopted by millions.",
    gif: "/2026/02_Defi_easy_wallet2_device.gif",
    video: "/2026/Defi_easy_wallet.mp4",
  },
  {
    id: "pwg",
    year: "2024",
    company: "Grab",
    role: "Principal Product Designer",
    link: "/case/grab",
    title: "Pay with Grab",
    caption: "FinTech・Strategy・Leading Pay with Grab vision",
    description: "Bringing contemporary consumer app patterns to everyday finance. A mobile redesign that elevated spending as a first-class citizen alongside investing — unifying payments across rides, food, groceries, and marketplace in Southeast Asia.",
    gif: "/2026/03_pwd_device.gif",
  },
]

export default function LaunchedProduct() {
  const [, navigate] = useLocation()
  const [idx, setIdx]         = useState(0)
  const [hovered, setHovered] = useState(false)
  const [overArrow, setOverArrow] = useState(false)
  const [popup, setPopup]     = useState(false)
  const [panelPos, setPanelPos]           = useState(null)
  const [displayedText, setDisplayedText] = useState("")
  const [typeDone, setTypeDone]           = useState(false)
  const deviceRef    = useRef(null)
  const btnRef       = useRef(null)
  const infoPanelRef = useRef(null)
  const rafRef       = useRef(null)
  const timerRef     = useRef(null)
  const n = PROJECTS.length
  const p = PROJECTS[idx]

  // Typewriter effect when panel opens
  useEffect(() => {
    clearTimeout(timerRef.current)
    if (popup) {
      setDisplayedText("")
      setTypeDone(false)
      let i = 0
      const text = p.description
      const tick = () => {
        i++
        setDisplayedText(text.slice(0, i))
        if (i < text.length) timerRef.current = setTimeout(tick, 16)
        else setTypeDone(true)
      }
      timerRef.current = setTimeout(tick, 80)
    } else {
      setDisplayedText("")
      setTypeDone(false)
    }
    return () => clearTimeout(timerRef.current)
  }, [popup, p.id])

  // Direct DOM update via RAF — pill follows cursor
  const handleMouseMove = useCallback((e) => {
    if (!deviceRef.current || !btnRef.current) return
    const rect = deviceRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      if (btnRef.current) {
        btnRef.current.style.left = `${x}px`
        btnRef.current.style.top  = `${y}px`
      }
    })
  }, [])

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  const go = (d) => {
    setHovered(false)
    setPopup(false)
    setIdx(i => (i + d + n) % n)
  }

  return (
    <div className="page-panel lp-panel">
      <div className="lp-page">

        {/* ── Left metadata ── */}
        <aside className="lp-meta" key={p.id}>
          <span className="lp-meta-tag">{p.year}・{p.company}・{p.role}</span>
          <h2 className="lp-meta-title">{p.title}</h2>
          <button className="lp-meta-link" onClick={() => navigate(p.link)}>View →</button>
        </aside>

        {/* ── Device GIF + flanking arrows ── */}
        <div className="lp-center">
          <div className="lp-col">
            <div
              ref={deviceRef}
              className={`lp-device${hovered ? " lp-device--hovered" : ""}`}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={(e) => {
                if (containsNode(infoPanelRef.current, e.relatedTarget)) return
                setHovered(false)
                setPopup(false)
                setPanelPos(null)
              }}
              onMouseMove={handleMouseMove}
              onClick={(e) => {
                if (popup) { setPopup(false); setPanelPos(null); return }
                const rect = deviceRef.current.getBoundingClientRect()
                setPanelPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
                setPopup(true)
              }}
            >
              <button className="lp-nav lp-nav--left" onClick={(e) => { e.stopPropagation(); go(-1) }} onMouseEnter={() => setOverArrow(true)} onMouseLeave={() => setOverArrow(false)} aria-label="Previous">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="lp-frame" key={p.id}>
                {p.video
                  ? <IPhoneDevice src={p.video} />
                  : <img src={p.gif} alt={p.title} className="lp-img" />
                }
              </div>

              {!p.video && <div className="lp-gradient" />}

              {/* Cursor-following "View more" pill */}
              <div
                ref={btnRef}
                className={`lp-cursor-btn${hovered && !overArrow ? " lp-cursor-btn--on" : ""}`}
              >
                View more ↗
              </div>

              {/* Info panel — anchored to click position, bottom-right of pill */}
              {popup && panelPos && (
                <div
                  ref={infoPanelRef}
                  className="lp-info-panel"
                  style={{ left: `${panelPos.x}px`, top: `${panelPos.y}px` }}
                  onMouseLeave={(e) => {
                    if (!containsNode(deviceRef.current, e.relatedTarget)) {
                      setHovered(false)
                      setPopup(false)
                      setPanelPos(null)
                    }
                  }}
                  onClick={e => { e.stopPropagation(); setPopup(false); setPanelPos(null) }}
                >
                  <p className="lp-info-text">
                    {displayedText}
                    {!typeDone && <span className="lp-info-cursor" />}
                  </p>
                </div>
              )}

              <button className="lp-nav lp-nav--right" onClick={(e) => { e.stopPropagation(); go(1) }} onMouseEnter={() => setOverArrow(true)} onMouseLeave={() => setOverArrow(false)} aria-label="Next">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Caption + dots — flows directly below device */}
            <div className="lp-footer">
              <span className="lp-caption">{p.caption}</span>
              <div className="lp-dots">
                {PROJECTS.map((_, i) => (
                  <button
                    key={i}
                    className={`lp-dot${i === idx ? " lp-dot--active" : ""}`}
                    onClick={() => { setHovered(false); setIdx(i) }}
                    aria-label={`Project ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
